import { useNavigator } from '@repo/toolkit/use-navigator'
import Button from '@repo/ui/button'
import { DuplicateIcon } from '@repo/ui/icons'
import Modal from '@repo/ui/modal'
import { useMutation } from '@tanstack/react-query'

import { getEnrollment } from '../../../../api/enrollments/get-enrollment'
import { Paths } from '../../../../lib/constants/path'
import { useModalManager } from '../../../../lib/hooks/use-modal-manager'
import { enrolmentDuplicationFields } from '../../../../lib/types/fields/enrollments/enrollment'
import { Enrollment } from '../../../../lib/types/models/enrollment/enrollment'
import { EnrollmentProperties } from '../../../../lib/types/models/enrollment/enrollment.properties'
import { showAxiosError } from '../../../../lib/utils/functions/misc.functions'
import { enrollmentInitialValues } from '../../../../views/enrollments/enrollment/enrollment-create'

type Props = {
  enrollment: Pick<Enrollment, 'id' | 'childFirstName' | 'childLastName' | 'isAA'>
}

const EnrollmentDuplicateButton = ({ enrollment }: Props) => {
  const { navigate } = useNavigator()
  const { modalState, toggleModal, closeModel } = useModalManager<'duplicate-enrollment'>([
    'duplicate-enrollment'
  ])

  const { mutate: duplicateEnrollmentMutation, isPending } = useMutation({
    mutationFn: () => getEnrollment(enrollment.id, enrolmentDuplicationFields),
    onError: showAxiosError,
    onSuccess: (data) => {
      // a duplicate is for a sibling: keep the last name, leave the first name empty
      const {
        childFirstName: _childFirstName,
        mainProposition,
        otoIndividualProposition,
        ...duplicatedEnrollment
      } = data
      // an OTO Individual enrollment has no main proposition
      const proposition =
        mainProposition && (({ insurances: _insurances, ...rest }) => rest)(mainProposition)
      navigate(Paths.ENROLLMENT_CREATE_PAGE, {
        state: {
          ...duplicatedEnrollment,
          ...(proposition && { mainProposition: proposition }),
          ...(otoIndividualProposition && { otoIndividualProposition }),
          ...enrollmentInitialValues
        }
      })
    }
  })

  return (
    <>
      <Button
        variant={'secondary'}
        icon={DuplicateIcon}
        label={'Dupliquer'}
        onClick={() => toggleModal('duplicate-enrollment')}
      />
      {/* duplicate enrollment modal */}
      <Modal
        icon={DuplicateIcon}
        variant={'confirmation'}
        isOpen={modalState['duplicate-enrollment']}
        isLoading={isPending}
        title={`Êtes-vous sûr de vouloir dupliquer le formulaire d'inscription de " ${EnrollmentProperties.getEnrollmentTitle(enrollment)} " ?`}
        actionButtonsOptions={{
          confirmText: 'Dupliquer',
          cancelText: 'Fermer',
          onConfirm: () => duplicateEnrollmentMutation()
        }}
        onCancel={() => closeModel('duplicate-enrollment')}
      />
    </>
  )
}

export default EnrollmentDuplicateButton
